<template>
    <div class="bg-gray-900 p-12 px-8 text-center lg:px-16">
        <div class="mx-auto max-w-4xl">
            <h1
                class="text-[28px] font-semibold leading-snug text-white antialiased lg:text-[40px]"
            >
                How do we create our questions?
            </h1>

            <div class="mt-6 leading-loose text-white">
                Our questions go through a thorough review process so you get
                the best NREMT test questions available anywhere.
            </div>
        </div>
    </div>
    <div class="container mb-24 flex max-w-7xl gap-8 lg:mx-auto">
        <div class="hidden w-80 flex-none pt-24 lg:block">
            <ul class="flex flex-col gap-1">
                <li>
                    <a
                        class="block h-12 w-full bg-gray-200 px-4 py-3"
                        href="#identify_a_need"
                        >1. Identify a need</a
                    >
                </li>
                <li>
                    <a
                        class="block h-12 w-full px-4 py-3"
                        href="#assign_a_qualified_writer"
                        >2. Assign a qualified writer</a
                    >
                </li>
                <li>
                    <a
                        class="block h-12 w-full px-4 py-3"
                        href="#exceed_nemses_standards"
                        >3. Exceed NEMSES standards</a
                    >
                </li>
                <li>
                    <a
                        class="block h-12 w-full px-4 py-3"
                        href="#question_review"
                        >4. Question Review</a
                    >
                </li>
                <li>
                    <a class="block h-12 w-full px-4 py-3" href="#published"
                        >5. Published</a
                    >
                </li>
            </ul>
        </div>
        <div class="px-8 pt-12 lg:border-l lg:border-gray-300 lg:pl-8 lg:pt-24">
            <div
                id="identify_a_need"
                class="mb-20 flex flex-col gap-4 lg:flex-row lg:gap-8"
            >
                <div
                    class="relative w-8 flex-none lg:left-[-1px] lg:-ml-8 lg:h-[68px] lg:w-16 lg:border-l lg:border-secondary-800 lg:pl-8"
                >
                    <div class="mr-8 w-8">
                        <img
                            src="/assets/art/question_review_process_1.png"
                            class="h-[48px] w-auto"
                        />
                    </div>
                </div>
                <div class="">
                    <h2 class="text-[28px] font-semibold md:text-[36px]">
                        Identify a need
                    </h2>
                    Our writers continuously review existing questions and check
                    for changes in the NREMT standards. When we identify a need,
                    the writing process begins.
                </div>
            </div>

            <div
                id="assign_a_qualified_writer"
                class="mb-20 flex flex-col gap-4 lg:flex-row lg:gap-8"
            >
                <div
                    class="relative w-8 flex-none lg:left-[-1px] lg:-ml-8 lg:h-[68px] lg:w-16 lg:border-l lg:border-secondary-800 lg:pl-8"
                >
                    <div class="mr-8 w-8">
                        <img
                            src="/assets/art/question_review_process_2.png"
                            class="h-[48px] w-auto"
                        />
                    </div>
                </div>
                <div class="">
                    <h2 class="text-[28px] font-semibold md:text-[36px]">
                        Assign a qualified writer
                    </h2>
                    All of our content is created by professional EMS
                    instructors across all EMS provider levels. We assign each
                    piece of content based on the writer's scope, area of
                    expertise, and depth of experience.
                </div>
            </div>

            <div
                id="exceed_nemses_standards"
                class="mb-20 flex flex-col gap-4 lg:flex-row lg:gap-8"
            >
                <div
                    class="relative w-8 flex-none lg:left-[-1px] lg:-ml-8 lg:h-[68px] lg:w-16 lg:border-l lg:border-secondary-800 lg:pl-8"
                >
                    <div class="mr-8 w-8">
                        <img
                            src="/assets/art/question_review_process_3.png"
                            class="h-[48px] w-auto"
                        />
                    </div>
                </div>
                <div class="">
                    <h2 class="text-[28px] font-semibold md:text-[36px]">
                        Exceed NEMSES Standards
                    </h2>
                    Our questions are written to exceed the National Emergency
                    Medical Education Standards (NEMSES). Each question on
                    EMTprep is built with the following criteria in mind:

                    <div class="mt-4">
                        <expanding-text-block
                            question="1. Difficulty Level"
                            answer="Difficulty levels ranging from 1-5 are assigned to each piece of content. 1 being a low level of difficulty and 5 being a high-level of difficulty"
                        />
                        <expanding-text-block
                            question="2. Blooms Taxonomy"
                            answer="Bloom's levels are assigned based on the learning objectives and foundational knowledge required to answer the question."
                        />
                        <expanding-text-block question="3. Focus Area">
                            Our questions are broken down into the same five
                            focus areas that the NREMT assigns their questions:
                            <ul class="list-disc pl-8">
                                <li>Airway, Respiration, & Ventilation</li>
                                <li>Cardiology & Resuscitation</li>
                                <li>Medical, Obstetrics, & Gynecology</li>
                                <li>Trauma</li>
                                <li>EMS Operations</li>
                            </ul>
                        </expanding-text-block>
                        <expanding-text-block
                            question="4. Sub-Category"
                            answer="We assign a sub-category to identify larger areas within a Focus area that the student is both weak and strong in."
                        />
                        <expanding-text-block
                            question="5. Topics"
                            answer="Topics are where we further drill down into sub-categories to show at the granular level where students need to spend their study time."
                        />
                        <expanding-text-block
                            question="6. Answer Rationale"
                            answer="Industry-exclusive video content providing students a walk-through of how to tackle each question."
                        />
                        <expanding-text-block
                            question="7. Video Rationale"
                            answer="At the conclusion of every quiz and exam is an answer rationale that outlines why the answers are correct and/or incorrect."
                        />
                    </div>
                </div>
            </div>

            <div
                id="question_review"
                class="mb-20 flex flex-col gap-4 lg:flex-row lg:gap-8"
            >
                <div
                    class="relative w-8 flex-none lg:left-[-1px] lg:-ml-8 lg:h-[68px] lg:w-16 lg:border-l lg:border-secondary-800 lg:pl-8"
                >
                    <div class="mr-8 w-8">
                        <img
                            src="/assets/art/question_review_process_4.png"
                            class="h-[48px] w-auto"
                        />
                    </div>
                </div>
                <div class="">
                    <h2 class="text-[28px] font-semibold md:text-[36px]">
                        Question Review
                    </h2>
                    Our team of EMS professionals runs multiple reviews on each
                    question to assess for accuracy, grammar, and relevance
                    <div class="mt-4">
                        <expanding-text-block
                            question="1. First Pass: Accuracy, Spelling, Grammar"
                            answer="During the first review questions are assessed for accuracy, grammar, spelling, and overall quality by our internal content team."
                        />
                        <expanding-text-block
                            question="2. Second Pass: Content Review"
                            answer="During the second review stage, questions will undergo an intensive review by a select group of EMS instructors, students, and providers."
                        />
                        <expanding-text-block
                            question="3. Final Review: Executive Team + Medical Director"
                            answer="We’re not finished yet. If a question has made it this far, it must still meet final approval by our overhead team and medical director. If it doesn't pass muster for any reason it goes back to the first step."
                        />
                    </div>
                </div>
            </div>

            <div
                id="published"
                class="mb-20 flex flex-col gap-4 lg:flex-row lg:gap-8"
            >
                <div
                    class="relative w-8 flex-none lg:left-[-1px] lg:-ml-8 lg:h-[68px] lg:w-16 lg:border-l lg:border-secondary-800 lg:pl-8"
                >
                    <div class="mr-8 w-8">
                        <img
                            src="/assets/art/question_review_process_5.png"
                            class="h-[48px] w-auto"
                        />
                    </div>
                </div>
                <div class="">
                    <h2 class="text-[28px] font-semibold md:text-[36px]">
                        Published
                    </h2>
                    After undergoing three reviews the question is finally ready
                    to go live to the EMTprep website. This comprehensive
                    process ensures you get the best questions available to
                    study for the NREMT.
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import FrontendLayout from "@/_Layouts/FrontendLayout";
import ExpandingTextBlock from "@/Components/ExpandingTextBlock.vue";

export default {
    name: "QuestionReviewProcess",
    components: {
        ExpandingTextBlock,
    },
    layout: FrontendLayout,
};
</script>
